import React from "react";
import { IVendor } from "../../../models/vendor.model";
import PaginationControls from "../../../utils/PaginationControls";

interface VendorsSectionProps {
  vendors: IVendor[];
  totalVendorItems: number;
  totalVendorPages: number;
  vendorPage: number;
  itemsPerPage: number;
  handleEdit: (type: "vendor", item: IVendor) => void;
  handleDelete: (
    type: "factory" | "vendor" | "material",
    id: string,
    name: string,
  ) => void;
  handleVendorPageChange: (page: number) => void;
}

const IconWrapper: React.FC<{
  icon: React.ElementType;
  className?: string;
}> = ({ icon: Icon, className }) => <Icon className={className} />;

const Button: React.FC<{
  type: string;
  onClick: () => void;
  className: string;
  children: React.ReactNode;
  disabled?: boolean;
}> = ({ type, onClick, className, children, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-md font-medium transition-colors ${className}`}
    disabled={disabled}
  >
    {children}
  </button>
);

const VendorsSection: React.FC<VendorsSectionProps> = ({
  vendors,
  totalVendorItems,
  totalVendorPages,
  vendorPage,
  itemsPerPage,
  handleEdit,
  handleDelete,
  handleVendorPageChange,
}) => {
  return (
    <div className="bg-white shadow overflow-x-auto sm:rounded-lg">
      <table className="min-w-full divide-y divide-gray-200 table-fixed w-full">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4"
            >
              Name
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4"
            >
              GST Number
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4"
            >
              Contact Number
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6"
            >
              Address
            </th>
            <th scope="col" className="px-6 py-3 text-right w-1/6">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {(vendors || []).map((vendor: IVendor) => (
            <tr key={vendor._id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <IconWrapper
                      icon={() => <span>🚚</span>}
                      className="h-5 w-5 text-green-600"
                    />
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900 whitespace-normal break-words">
                      {vendor.name}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-500 whitespace-normal break-words">
                {vendor.gstNumber}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {vendor.contactNumber}
              </td>
              <td className="px-6 py-4 text-sm text-gray-500 whitespace-normal break-words">
                {vendor.address}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  className="text-indigo-600 hover:text-indigo-900 mr-4"
                  onClick={() => handleEdit("vendor", vendor)}
                >
                  <IconWrapper icon={() => <span>✏️</span>} />
                </button>
                <button
                  className="text-red-600 hover:text-red-900"
                  onClick={() =>
                    handleDelete("vendor", vendor._id, vendor.name)
                  }
                >
                  <IconWrapper icon={() => <span>🗑️</span>} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-gray-200">
        <PaginationControls
          currentPage={vendorPage}
          totalPages={totalVendorPages}
          onPageChange={handleVendorPageChange}
          totalItems={totalVendorItems}
        />
      </div>
    </div>
  );
};

export default VendorsSection;
